import { useContext, useEffect, useId, useRef, useState, type CSSProperties, type PointerEvent, type ReactNode } from 'react';
import { advanceOrientation, padDeflection, type Orientation, type PadDeflection } from './orientation-pad';
import './camera-orientation-pad.css';
import { ControlGestureContext } from '../../components/ControlGestureContext';

export function CameraOrientationPad({ values, onChange, disabled = false, children, compact = false }: {
  values: Orientation; onChange: (values: Partial<Orientation>) => void; disabled?: boolean; children?: ReactNode; compact?: boolean;
}) {
  const help = useId();
  const group = useContext(ControlGestureContext);
  const currentGroup = useRef(group); currentGroup.current = group;
  const latest = useRef({ values, onChange }); latest.current = { values, onChange };
  const current = useRef(values);
  const gesture = useRef<{ pointer: number; x: number; y: number; last: number; emitted: number } | undefined>(undefined);
  const push = useRef<PadDeflection>({ x: 0, y: 0 });
  const frame = useRef<number | undefined>(undefined);
  const [deflection, setDeflection] = useState(push.current);
  const [active, setActive] = useState(false);
  const stop = () => {
    if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    if (gesture.current) currentGroup.current.end();
    frame.current = undefined; gesture.current = undefined; push.current = { x: 0, y: 0 };
    setDeflection(push.current); setActive(false);
  };
  useEffect(() => {
    const release = () => stop();
    const hidden = () => { if (document.hidden) stop(); };
    window.addEventListener('blur', release); document.addEventListener('visibilitychange', hidden);
    return () => {
      if (frame.current !== undefined) cancelAnimationFrame(frame.current);
      if (gesture.current) currentGroup.current.end();
      window.removeEventListener('blur', release); document.removeEventListener('visibilitychange', hidden);
    };
  }, []);
  useEffect(() => { if (disabled) stop(); }, [disabled]);
  const tick = (now: number) => {
    const held = gesture.current; if (!held) return;
    current.current = advanceOrientation(current.current, push.current, (now - held.last) / 1000); held.last = now;
    if (now - held.emitted >= 50) {
      const changed: Partial<Orientation> = {};
      for (const axis of ['tilt', 'roll'] as const) {
        const value = Math.round(current.current[axis] * 10) / 10;
        if (value !== latest.current.values[axis] && push.current[axis === 'tilt' ? 'y' : 'x'] !== 0) changed[axis] = value;
      }
      if (Object.keys(changed).length) latest.current.onChange(changed);
      held.emitted = now;
    }
    frame.current = requestAnimationFrame(tick);
  };
  const down = (event: PointerEvent<HTMLButtonElement>) => {
    if (disabled || gesture.current || event.button !== 0) return;
    event.preventDefault(); event.currentTarget.focus({ preventScroll: true }); event.currentTarget.setPointerCapture(event.pointerId);
    current.current = { ...latest.current.values };
    const now = performance.now();
    gesture.current = { pointer: event.pointerId, x: event.clientX, y: event.clientY, last: now, emitted: now };
    currentGroup.current.begin(); setActive(true); frame.current = requestAnimationFrame(tick);
  };
  const move = (event: PointerEvent<HTMLButtonElement>) => {
    const held = gesture.current; if (!held || event.pointerId !== held.pointer) return;
    push.current = padDeflection(event.clientX - held.x, event.clientY - held.y); setDeflection(push.current);
  };
  const release = (event: PointerEvent<HTMLButtonElement>) => { if (event.pointerId === gesture.current?.pointer) stop(); };
  return <div className={`camera-orientation${compact ? ' orientation-compact' : ''}`} role="group" aria-label="Tilt and bank control">
    <div className="orientation-heading"><strong>Tilt / pitch · Bank / roll</strong><span>{active ? 'Adjusting' : 'Hold + push'}</span></div>
    <div className="orientation-layout"><div className="orientation-stick">
    <button type="button" className="orientation-pad" aria-label="Tilt and bank joystick" aria-describedby={help} disabled={disabled}
      data-active={active} style={{ '--push-x': deflection.x, '--push-y': deflection.y, '--bank': `${values.roll}deg` } as CSSProperties}
      onPointerDown={down} onPointerMove={move} onPointerUp={release} onPointerCancel={release} onLostPointerCapture={release}
      onBlur={stop}
      onKeyDown={event => {
        const axis = event.key === 'ArrowLeft' || event.key === 'ArrowRight' ? 'roll' : event.key === 'ArrowUp' || event.key === 'ArrowDown' ? 'tilt' : undefined;
        if (event.key === 'Escape') { stop(); return; }
        if (!axis) return;
        event.preventDefault(); event.stopPropagation();
        const direction = event.key === 'ArrowUp' || event.key === 'ArrowRight' ? 1 : -1;
        const next = advanceOrientation(latest.current.values, { x: axis === 'roll' ? direction : 0, y: axis === 'tilt' ? direction : 0 }, (event.shiftKey ? .1 : 1) / 30);
        currentGroup.current.begin(); onChange({ [axis]: Math.round(next[axis] * 10) / 10 }); currentGroup.current.end();
      }}>
      <span className="orientation-cross" aria-hidden="true" /><span className="orientation-ring" aria-hidden="true" />
      <span className="orientation-north" aria-hidden="true">Tilt +</span><span className="orientation-south" aria-hidden="true">Tilt −</span>
      <span className="orientation-west" aria-hidden="true">−</span><span className="orientation-east" aria-hidden="true">+</span>
      <span className="orientation-horizon" aria-hidden="true" /><span className="orientation-push" aria-hidden="true" />
      <span className="orientation-grip" aria-hidden="true"><span /></span>
    </button>
    {!compact && <div className="orientation-readouts"><output>Tilt {values.tilt.toFixed(1)}°</output><output>Bank {values.roll.toFixed(1)}°</output></div>}
    </div>{children}</div>
    <details className="orientation-instructions" open={compact ? undefined : true}><summary>Joystick help</summary><p id={help}>Hold and push slightly. Up/down changes tilt; left/right changes bank. Release to hold the angles. Arrow keys nudge 1°; Shift nudges 0.1°.</p></details>
    <div className="orientation-actions"><button type="button" disabled={disabled} onClick={() => onChange({ roll: 0 })}>Level bank</button>
      <button type="button" disabled={disabled} onClick={() => onChange({ tilt: 0 })}>Straight down</button></div>
  </div>;
}
